import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { buildModernChartConfig, buildEmbeddedChartConfig } from '../utils/chartConfig';

interface UseChartInstanceParams {
  isDark: boolean;
  axisColor: string;
  chartData: {
    sortedModels: string[];
    variable: { displayUnit: string };
    yAxisRange: { min: number | undefined; max: number | undefined };
    series: any[];
  } | null;
  timezone: string;
  formatInUserTimezone: (timestamp: string, format: string) => string;
}

export function useChartInstance({
  isDark,
  axisColor,
  chartData,
  timezone,
  formatInUserTimezone,
}: UseChartInstanceParams) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // Initialize chart instance
  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, {
        renderer: 'canvas',
      });
    }

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  // Update chart when data changes
  useEffect(() => {
    if (!chartData || !chartInstance.current) {
      return;
    }

    const { sortedModels, variable, yAxisRange, series } = chartData;

    const option = buildModernChartConfig({
      isDark,
      axisColor,
      sortedModels,
      variable,
      yAxisRange,
      series,
      timezone,
      formatInUserTimezone,
    });

    chartInstance.current.clear();
    chartInstance.current.setOption(option);
  }, [chartData, isDark, axisColor, timezone, formatInUserTimezone]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    chartRef,
    chartInstance,
  };
}

export function useEmbeddedChartInstance({
  isDark,
  axisColor,
  chartData,
  timezone,
  formatInUserTimezone,
}: UseChartInstanceParams) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // Initialize chart instance
  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, {
        renderer: 'canvas',
      });
    }

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  // Update chart when data changes
  useEffect(() => {
    if (!chartData || !chartInstance.current) {
      return;
    }

    const { sortedModels, variable, yAxisRange, series } = chartData;

    const option = buildEmbeddedChartConfig({
      isDark,
      axisColor,
      sortedModels,
      variable,
      yAxisRange,
      series,
      timezone,
      formatInUserTimezone,
    });

    chartInstance.current.clear();
    chartInstance.current.setOption(option);
  }, [chartData, isDark, axisColor, timezone, formatInUserTimezone]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    chartRef,
    chartInstance,
  };
}
